import Phaser from 'phaser'
import { CANDY_ASSETS, CANDY_AUDIO } from '@/game/assets/assetManifest'
import { GameContext } from '@/config/GameContext'

/**
 * PreloadScene — 加载 candy 完整 manifest（图片 + 音频），显示 candy 风格
 * loading 屏（背景 = nc_bg_loading_portal_logo_v01.png）。
 */
export class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'PreloadScene' })
  }

  preload(): void {
    this.drawLoadingUI()

    // 加载策略：永远先加载 candy 完整 UI（buttons/frame/HUD/FX），作为所有游戏的 UI fallback；
    // 然后加载当前 game 自己的 backgrounds + symbols + audio。
    // 这样 sakura 也能用现成 candy UI 壳子先跑起来，再逐步替换为各自主题资源。
    const cfg = GameContext.config()
    for (const a of CANDY_ASSETS) {
      if (this.textures.exists(a.key)) continue
      this.load.image(a.key, a.path)
    }
    if (cfg.id === 'candy') {
      for (const a of CANDY_AUDIO) {
        this.load.audio(a.key, a.path)
      }
    } else {
      // 当前 game 自己的资源（backgrounds + symbols + popups + fx + 完整 audio）。
      // sakura 完全独立，不再 fallback 到 candy SFX/BGM —
      // 每个 game 的 manifest.audio 必须自带 spin/stop/win/free_spins SFX。
      for (const img of cfg.images) {
        if (this.textures.exists(img.key)) continue
        this.load.image(img.key, img.path)
      }
      for (const a of cfg.audio) {
        this.load.audio(a.key, a.path)
      }
    }
  }

  create(): void {
    this.scene.start('GameScene')
  }

  private drawLoadingUI(): void {
    // 用真实 canvas 尺寸（main.ts 里按 host aspect 撑过 design 尺寸），不用 design 常量。
    // 否则 splash 只覆盖 1080×1920 design 矩形，stageSize 多出的那块就是 #13002a 紫黑底。
    const W = this.scale.gameSize.width
    const H = this.scale.gameSize.height
    const cfg = GameContext.config()

    // 当前 game 自己的 loading bg（BootScene 已先 fetch 这一张）；缺失则纯色 fallback
    const loadingKey = cfg.bgLoadingKey ?? cfg.bgMainKey
    if (this.textures.exists(loadingKey)) {
      const bg = this.add.image(W / 2, H / 2, loadingKey).setOrigin(0.5)
      const tex = bg.texture.getSourceImage()
      const scale = Math.max(W / tex.width, H / tex.height)
      bg.setScale(scale)
    } else {
      this.cameras.main.setBackgroundColor(this.fallbackBgColor(cfg.id))
    }

    // 每个 game 自己的主题色（决定进度条 + 文字风格）
    const theme = this.themeFor(cfg.id)

    // 进度条
    const barW = 720
    const barH = 36
    const barX = (W - barW) / 2
    const barY = H * 0.66

    const frame = this.add.graphics()
    frame.lineStyle(4, theme.barStroke, 1)
    frame.fillStyle(0xffffff, 0.45)
    frame.fillRoundedRect(barX, barY, barW, barH, 16)
    frame.strokeRoundedRect(barX, barY, barW, barH, 16)

    const fill = this.add.graphics()
    const updateFill = (p: number): void => {
      const fillW = (barW - 8) * p
      fill.clear()
      fill.fillGradientStyle(theme.barFill0, theme.barFill1, theme.barFill1, theme.barFill0, 1)
      if (fillW > 0) fill.fillRoundedRect(barX + 4, barY + 4, fillW, barH - 8, 12)
    }

    const pctText = this.add
      .text(W / 2, barY + barH + 36, '0%', {
        fontFamily: 'Arial Black, Impact, sans-serif',
        fontSize: '32px',
        color: theme.pctColor,
        stroke: '#ffffff',
        strokeThickness: 4,
      })
      .setOrigin(0.5)

    const tipText = this.add
      .text(W / 2, barY + barH + 90, theme.tip, {
        fontFamily: 'Arial, sans-serif',
        fontSize: '24px',
        color: theme.tipColor,
      })
      .setOrigin(0.5)

    this.load.on('progress', (p: number) => {
      updateFill(p)
      pctText.setText(`${Math.floor(p * 100)}%`)
    })
    this.load.on('complete', () => {
      frame.destroy()
      fill.destroy()
      pctText.destroy()
      tipText.destroy()
    })
  }

  /** 每个 game 的 loading 屏主题（进度条配色 + tip 文案）。*/
  private themeFor(id: string): {
    barStroke: number
    barFill0: number
    barFill1: number
    pctColor: string
    tipColor: string
    tip: string
  } {
    switch (id) {
      case 'sakura':
        return {
          barStroke: 0xf472b6,
          barFill0: 0xffffff,
          barFill1: 0xf472b6,
          pctColor: '#be185d',
          tipColor: '#fce7f3',
          tip: 'Loading Sakura Neon Fortune…',
        }
      case 'candy':
      default:
        return {
          barStroke: 0xec4899,
          barFill0: 0xffd166,
          barFill1: 0xec4899,
          pctColor: '#db2777',
          tipColor: '#a855f7',
          tip: 'Loading Neon Candy Fortune…',
        }
    }
  }

  /** loading bg 缺失时的纯色 fallback */
  private fallbackBgColor(id: string): string {
    switch (id) {
      case 'sakura':
      case 'candy':
      default:        return '#fdf2f8'
    }
  }
}
